/**
 * usePersistentState — Estado React que sobrevive a recargas de página.
 *
 * Igual que useState pero sincronizado con localStorage bajo una clave dada.
 * Útil para flujos anti-fraude / anti-abandono donde el usuario NO debe poder
 * esquivar una pantalla recargando (ej: pantalla obligatoria de envío de
 * reporte tras cerrar caja).
 *
 * Ejemplo:
 *   const [cierrePendiente, setCierrePendiente] =
 *     usePersistentState<CierreCaja | null>("caja.pendingWhatsApp", null);
 *
 * Reglas:
 *   - Si el valor actual es `null` (o undefined), se elimina la clave del
 *     storage. Así no queda basura cuando el flujo termina.
 *   - JSON.parse errores → vuelve al default (no crashea la app).
 *   - SSR safe: si `window` no existe, se comporta como useState normal.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Evento custom disparado cuando una instancia de usePersistentState escribe.
 * Permite sincronizar OTRAS instancias del mismo hook en la MISMA pestaña
 * (el evento nativo `storage` solo se dispara entre pestañas distintas).
 *
 * Sin esto, si Componente A hace `set(valor)` y Componente B también usa la
 * misma clave, B NO se enteraría hasta una recarga completa. Crítico para el
 * overlay obligatorio de WhatsApp tras cerrar caja.
 */
const PERSISTENT_STATE_EVENT = "altenot:persistent-state-change";

interface PersistentStateEventDetail {
  key: string;
  value: unknown;
}

export function usePersistentState<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  const keyRef = useRef(key);
  keyRef.current = key;

  // Persistir a localStorage + notificar a OTRAS instancias del mismo hook
  // (en la misma pestaña) que la clave cambió.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (state === null || state === undefined) {
        window.localStorage.removeItem(keyRef.current);
      } else {
        window.localStorage.setItem(keyRef.current, JSON.stringify(state));
      }
      window.dispatchEvent(
        new CustomEvent<PersistentStateEventDetail>(PERSISTENT_STATE_EVENT, {
          detail: { key: keyRef.current, value: state },
        }),
      );
    } catch {
      /* quota exceeded, privacy mode, etc. — silencioso */
    }
  }, [state]);

  // Escuchar cambios de otras instancias (mismo tab via CustomEvent,
  // otras pestañas via evento `storage` nativo).
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<PersistentStateEventDetail>).detail;
      if (!detail || detail.key !== keyRef.current) return;
      setState(detail.value as T);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== keyRef.current) return;
      try {
        if (e.newValue === null) {
          setState(defaultValue);
        } else {
          setState(JSON.parse(e.newValue) as T);
        }
      } catch {
        setState(defaultValue);
      }
    };

    window.addEventListener(PERSISTENT_STATE_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(PERSISTENT_STATE_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
    // defaultValue intencionalmente omitido: solo importa la clave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = useCallback((value: T | ((prev: T) => T)) => {
    setState(value);
  }, []);

  return [state, set];
}
